import { adminFetch, buildListParams, normalizeList } from "./_client";
import type { AdminListParams, AdminListResponse, VehicleMake, SearchScoringWeight, VehicleModel, VehicleSubmodel, VehicleMakesPage } from "./types";


export function fetchSearchScoring(token: string) {
  return adminFetch<{ weights: SearchScoringWeight[] }>("/admin/search-scoring", token);
}


export function updateSearchScoring(token: string, weights: Record<string, number>) {
  return adminFetch<{ success: boolean }>("/admin/search-scoring", token, {
    method: "PUT",
    body: JSON.stringify({ weights }),
  });
}


export function triggerSearchIndexRebuild(token: string) {
  return adminFetch<{ success: boolean; message: string }>("/admin/search-scoring/rebuild-index", token, {
    method: "POST",
  });
}


export function fetchVehicleMakes(
  token: string,
  search?: string,
  classification?: string,
  page?: number,
  limit?: number,
) {
  const params = new URLSearchParams();
  if (search) params.set("q", search);
  if (classification) params.set("classification", classification);
  if (page) params.set("page", String(page));
  if (limit) params.set("limit", String(limit));
  const qs = params.toString();
  return adminFetch<VehicleMakesPage>( `/admin/vehicle-db/makes${qs ? `?${qs}` : ""}`, token);
}


export async function fetchVehicleMakesPaged(
  token: string,
  params: AdminListParams & { classification?: string } = {},
): Promise<AdminListResponse<VehicleMake>> {
  const sp = buildListParams({ page: 1, ...params });
  if (params.classification) sp.set("classification", params.classification);
  const raw = await adminFetch<Record<string, unknown>>(`/admin/vehicle-db/makes?${sp}`, token);
  return normalizeList<VehicleMake>(raw, "makes");
}


export function createVehicleMake(token: string, data: { name: string; is_premier?: boolean; is_exotic?: boolean; display_order?: number }) {
  return adminFetch<VehicleMake>("/admin/vehicle-db/makes", token, { method: "POST", body: JSON.stringify(data) });
}


export function updateVehicleMake(token: string, id: number, data: Partial<{ name: string; is_premier: boolean; is_exotic: boolean; display_order: number }>) {
  return adminFetch<VehicleMake>( `/admin/vehicle-db/makes/${id}`, token, { method: "PUT", body: JSON.stringify(data) });
}


export function deleteVehicleMake(token: string, id: number) {
  return adminFetch<{ success: boolean }>( `/admin/vehicle-db/makes/${id}`, token, { method: "DELETE" });
}


export function fetchVehicleModels(token: string, makeId: number) {
  return adminFetch<VehicleModel[]>( `/admin/vehicle-db/makes/${makeId}/models`, token);
}


export function fetchVehicleMakeById(token: string, id: number) {
  return adminFetch<VehicleMake>(`/admin/vehicle-db/makes/${id}`, token);
}


export function fetchVehicleModelById(token: string, id: number) {
  return adminFetch<VehicleModel>(`/admin/vehicle-db/models/${id}`, token);
}


export function createVehicleModel(token: string, makeId: number, name: string) {
  return adminFetch<VehicleModel>( `/admin/vehicle-db/makes/${makeId}/models`, token, { method: "POST", body: JSON.stringify({ name }) });
}


export function updateVehicleModel(token: string, id: number, name: string) {
  return adminFetch<VehicleModel>( `/admin/vehicle-db/models/${id}`, token, { method: "PUT", body: JSON.stringify({ name }) });
}


export function deleteVehicleModel(token: string, id: number) {
  return adminFetch<{ success: boolean }>( `/admin/vehicle-db/models/${id}`, token, { method: "DELETE" });
}


export function fetchVehicleSubmodels(token: string, modelId: number) {
  return adminFetch<VehicleSubmodel[]>( `/admin/vehicle-db/models/${modelId}/submodels`, token);
}


export function createVehicleSubmodel(token: string, modelId: number, name: string) {
  return adminFetch<VehicleSubmodel>( `/admin/vehicle-db/models/${modelId}/submodels`, token, { method: "POST", body: JSON.stringify({ name }) });
}


export function updateVehicleSubmodel(token: string, id: number, name: string) {
  return adminFetch<VehicleSubmodel>( `/admin/vehicle-db/submodels/${id}`, token, { method: "PUT", body: JSON.stringify({ name }) });
}


export function deleteVehicleSubmodel(token: string, id: number) {
  return adminFetch<{ success: boolean }>( `/admin/vehicle-db/submodels/${id}`, token, { method: "DELETE" });
}


export function batchClassifyMakes(token: string, ids: number[], classification: { is_premier?: boolean; is_exotic?: boolean }) {
  return adminFetch<{ success: boolean }>("/admin/vehicle-db/makes/batch-classify", token, { method: "POST", body: JSON.stringify({ ids, ...classification }) });
}
